import React, { useMemo } from "react";
import Card from "components/ui/Card/Card";
import DataTable, {
  DataTableColumn,
} from "components/ui/DataTable/DataTable";
import { formatCurrency } from "utils/format";
import { formatDateTime } from "utils/dateUtils";
import styles from "./BkashTransactionsPage.module.scss";

type BkashTransaction = {
  id: string;
  dateTime: string;
  accountNo: string;
  debitMsisdn: string;
  creditOrgName: string;
  creditShortCode: string;
  trxId: string;
  transactionStatus: "Completed" | "Pending" | "Failed" | "Reversed";
  transactionType: "Recharge" | "Refund" | "Reversal";
  currency: "BDT" | "USD";
  amount: number;
  transactionReference: string;
};

const transactions: BkashTransaction[] = [
  {
    id: "1",
    dateTime: "2024-03-01T09:45:00",
    accountNo: "AC-10045",
    debitMsisdn: "01711000111",
    creditOrgName: "WaterATM",
    creditShortCode: "WATM01",
    trxId: "BK20240301001",
    transactionStatus: "Completed",
    transactionType: "Recharge",
    currency: "BDT",
    amount: 2500,
    transactionReference: "Recharge via app",
  },
  {
    id: "2",
    dateTime: "2024-03-01T10:12:00",
    accountNo: "AC-10046",
    debitMsisdn: "01712000222",
    creditOrgName: "WaterATM",
    creditShortCode: "WATM01",
    trxId: "BK20240301012",
    transactionStatus: "Completed",
    transactionType: "Recharge",
    currency: "BDT",
    amount: 1800,
    transactionReference: "Self recharge",
  },
  {
    id: "3",
    dateTime: "2024-03-02T08:58:00",
    accountNo: "AC-10047",
    debitMsisdn: "01713000333",
    creditOrgName: "WaterATM",
    creditShortCode: "WATM01",
    trxId: "BK20240302008",
    transactionStatus: "Pending",
    transactionType: "Recharge",
    currency: "BDT",
    amount: 2000,
    transactionReference: "Pending confirmation",
  },
  {
    id: "4",
    dateTime: "2024-03-02T12:20:00",
    accountNo: "AC-10048",
    debitMsisdn: "01714000444",
    creditOrgName: "WaterATM",
    creditShortCode: "WATM01",
    trxId: "BK20240302122",
    transactionStatus: "Failed",
    transactionType: "Recharge",
    currency: "BDT",
    amount: 3500,
    transactionReference: "Insufficient balance",
  },
  {
    id: "5",
    dateTime: "2024-03-03T09:10:00",
    accountNo: "AC-10049",
    debitMsisdn: "01715000555",
    creditOrgName: "WaterATM",
    creditShortCode: "WATM01",
    trxId: "BK20240303010",
    transactionStatus: "Reversed",
    transactionType: "Reversal",
    currency: "BDT",
    amount: 2500,
    transactionReference: "Reverse duplicate",
  },
];

function BkashTransactionsPage() {
  const columns = useMemo<DataTableColumn<BkashTransaction>[]>(
    () => [
      {
        id: "dateTime",
        header: "Date Time",
        accessorKey: "dateTime",
        filter: {
          type: "date",
          fromPlaceholder: "From date",
          toPlaceholder: "To date",
        },
        cell: (value) => <span>{formatDateTime(value)}</span>,
      },
      {
        id: "accountNo",
        header: "Account No",
        accessorKey: "accountNo",
        filter: { type: "text", placeholder: "Account number" },
        enableGlobalFilter: true,
      },
      {
        id: "debitMsisdn",
        header: "Debit MSISDN",
        accessorKey: "debitMsisdn",
        filter: { type: "text", placeholder: "Debit MSISDN" },
        enableGlobalFilter: true,
      },
      {
        id: "creditOrgName",
        header: "Credit Organization Name",
        accessorKey: "creditOrgName",
        enableGlobalFilter: true,
      },
      {
        id: "creditShortCode",
        header: "Credit Short Code",
        accessorKey: "creditShortCode",
      },
      {
        id: "trxId",
        header: "TrxID",
        accessorKey: "trxId",
        filter: { type: "text", placeholder: "Transaction ID" },
        enableGlobalFilter: true,
      },
      {
        id: "transactionStatus",
        header: "Transaction Status",
        accessorKey: "transactionStatus",
      },
      {
        id: "transactionType",
        header: "Transaction Type",
        accessorKey: "transactionType",
      },
      {
        id: "currency",
        header: "Currency",
        accessorKey: "currency",
        align: "center",
      },
      {
        id: "amount",
        header: "Amount",
        accessorKey: "amount",
        cell: (value) => <span>{formatCurrency(Number(value ?? 0))}</span>,
        align: "right",
      },
      {
        id: "transactionReference",
        header: "Transaction Reference",
        accessorKey: "transactionReference",
      },
    ],
    []
  );

  return (
    <div className={styles.page}>
      <Card>
        <div className={styles.header}>
          <h1 className={styles.title}>Bkash Transaction</h1>
          <p className={styles.subtitle}>
            Filter and review transactions processed through the bKash gateway.
          </p>
        </div>

        <DataTable
          columns={columns}
          data={transactions}
          pageSizeOptions={[5, 10, 25]}
          initialPageSize={5}
          globalSearchPlaceholder="Search by account, MSISDN, or transaction ID"
          emptyState="No bKash transactions found for the selected filters."
        />
      </Card>
    </div>
  );
}

export default BkashTransactionsPage;
